import { FC, useEffect, useState } from 'react';
import { MessageCircleX, Loader2, Eye } from 'lucide-react';
import { TQuizDataTableRow } from '../../../../types/analytics.types';
import AnalyticsService from '../../../../services/analytics.service';
import StudentQuizData from './StudentQuizData';

const thStyle =
  'p-3 min-w-[100px] sticky top-0 bg-[white] text-appTextLight font-medium z-[1]';

const getLocaleDateTime = (dateStr: string | null) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

interface QuizDataTableProps {
  quizId: string;
}

const QuizDataTable: FC<QuizDataTableProps> = ({ quizId }) => {
  const [rows, setRows] = useState<TQuizDataTableRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedStudentName, setSelectedStudentName] = useState<string>('');

  useEffect(() => {
    setLoading(true);
    AnalyticsService.fetchQuizDataTable({ quizId })
      .then((res: { data: { data: TQuizDataTableRow[] | null } }) => {
        setRows(res.data?.data ?? []);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [quizId]);

  if (loading) {
    return (
      <div className="mt-4 w-full flex justify-center items-center py-20">
        <Loader2 className="animate-spin text-gray-400" size={40} />
      </div>
    );
  }

  return (
    <div className="mt-4 w-full pb-5">
      {/* Show StudentQuizData when a user is selected */}
      {selectedUserId && (
        <div className="mb-4">
          <StudentQuizData
            userId={selectedUserId}
            studentName={selectedStudentName}
            onClose={() => {
              setSelectedUserId(null);
              setSelectedStudentName('');
            }}
          />
        </div>
      )}

      {!selectedUserId && (
      <div className="overflow-y-auto max-h-[600px] border border-appLightBlue rounded-lg bg-white">
        <div className="font-medium text-[16px] p-4 mb-2 border-b-appLightBlue border-b">
          Student Quiz Data
        </div>

        {rows.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="text-[14px] bg-white min-w-full">
              <thead>
                <tr className="text-[#616161] text-left">
                  <th className={thStyle}>Student Name</th>
                  <th className={thStyle}>Attempted</th>
                  <th className={thStyle}>Correct</th>
                  <th className={thStyle}>Incorrect</th>
                  <th className={thStyle}>Time</th>
                  <th className={thStyle}>Date-time</th>
                  <th className={thStyle}>Score</th>
                  <th className={thStyle}>View Details</th>
                </tr>
              </thead>
              <tbody className="text-[#333] font-regular">
                {rows.map((row, index) => (
                  <tr
                    className="border-b border-[#E2E8F0] hover:bg-gray-50"
                    key={index}
                  >
                    <td className="px-3 py-2 max-w-[220px]">
                      <span
                        className="text-[14px] font-semibold text-[#020817] truncate block"
                        title={row.studentName}
                      >
                        {row.studentName}
                      </span>
                    </td>
                    <td className="px-3 py-2">{row.attemptedQuestions}</td>
                    <td className="px-3 py-2 text-green-600 font-medium">
                      {row.correct}
                    </td>
                    <td className="px-3 py-2 text-red-600 font-medium">
                      {row.incorrect}
                    </td>
                    <td className="px-3 py-2">{row.formattedTime}</td>
                    <td className="px-3 py-2">
                      {getLocaleDateTime(row.dateTime)}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`font-medium ${
                          row.score >= 70
                            ? 'text-green-600'
                            : row.score >= 50
                              ? 'text-orange-600'
                              : 'text-red-600'
                        }`}
                      >
                        {row.score}%
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <Eye
                        size={18}
                        className="text-black cursor-pointer"
                        onClick={() => {
                          setSelectedUserId(row.userId);
                          setSelectedStudentName(row.studentName);
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-5">
            <div className="flex flex-col justify-center items-center mt-10">
              <MessageCircleX color="black" size={100} />
              <p className="text-[14px] font-medium text-[#020817] mt-3">
                No quiz data found.
              </p>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default QuizDataTable;
